'use client'

import { useState, useRef, useCallback, useEffect, type DragEvent } from 'react'
import { toast } from 'sonner'
import { useFreemium } from '@/lib/freemium-context'
import { CreditCostBanner } from '@/components/CreditCostBanner'
import { friendlyError } from '@/lib/error-messages'
import { AUTH_RESUME_SETTLE_MS } from '@/lib/auth-resume'
import { useModuleDocuments } from '@/hooks/use-module-documents'
import ModuleHistoryPanel from '@/components/transqual/ModuleHistoryPanel'
import {
  Upload,
  FileText,
  Loader2,
  Copy,
  RotateCcw,
  Sparkles,
  Check,
  FileDown,
  Send,
  PencilLine,
  FileType2,
  X,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '@/components/ui/alert-dialog'
import { Separator } from '@/components/ui/separator'
import { REFERENCE_PRICE_LABEL } from '@/lib/pricing-display'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ACCEPTED_EXTENSIONS = ['.pdf', '.docx', '.txt']
// Valeur de l'attribut accept = extensions + types MIME, pour que le sélecteur
// (mobile/Android notamment) ne grise pas ces fichiers. La validation continue
// d'utiliser ACCEPTED_EXTENSIONS (par extension) — inchangée.
const ACCEPT_ATTR = [
  ...ACCEPTED_EXTENSIONS,
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
].join(',')
const MAX_FILE_SIZE_MB = 30
// Clé localStorage normalisée (Module 01 = Note Technique).
// LEGACY_LS_KEY conservée pour migration silencieuse depuis l'ancien format.
const LS_KEY = 'qualyse_module01_result'
const LEGACY_LS_KEY = 'qualyse_note_technique_draft'
const GENERATE_TIMEOUT_MS = 600_000

// ---------------------------------------------------------------------------
// Stepper
// ---------------------------------------------------------------------------

const M_COLOR = '#2d6614'
const M_BG = '#f0faf0'
const M_STEPS = [
  { name: 'Importer', hint: 'TDR (PDF, DOCX, TXT)' },
  { name: 'Générer', hint: 'Note technique' },
  { name: 'Résultat', hint: 'Télécharger & affiner' },
]

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function extractDocumentStructure(text: string): { level: 1 | 2 | 3; title: string }[] {
  const structure: { level: 1 | 2 | 3; title: string }[] = []
  for (const line of text.split('\n')) {
    const t1 = line.match(/^\[TITRE 1\]\s*(.+)/)
    if (t1) { structure.push({ level: 1, title: t1[1].trim() }); continue }
    const t2 = line.match(/^\[TITRE 2\]\s*(.+)/)
    if (t2) { structure.push({ level: 2, title: t2[1].trim() }); continue }
    const t3 = line.match(/^\[TITRE 3\]\s*(.+)/)
    if (t3) { structure.push({ level: 3, title: t3[1].trim() }); continue }
  }
  return structure
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 o'
  const units = ['o', 'Ko', 'Mo', 'Go']
  const k = 1024
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  const value = bytes / Math.pow(k, i)
  return `${value.toFixed(i === 0 ? 0 : 1)} ${units[i]}`
}

function getFileExtension(filename: string): string {
  return ('.' + filename.split('.').pop()?.toLowerCase()) as string
}

function isValidFileType(file: File): boolean {
  const ext = getFileExtension(file.name)
  return ACCEPTED_EXTENSIONS.includes(ext)
}

function countWords(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length
}

function formatNumber(n: number): string {
  return n.toLocaleString('fr-FR')
}

async function extractTextFromFile(file: File): Promise<string> {
  const ext = getFileExtension(file.name)
  switch (ext) {
    case '.txt':
      return file.text()
    case '.pdf': {
      const pdfjsLib = await import('pdfjs-dist')
      pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
        'pdfjs-dist/build/pdf.worker.min.mjs',
        import.meta.url,
      ).toString()
      const buffer = await file.arrayBuffer()
      const pdf = await pdfjsLib.getDocument({ data: buffer }).promise
      const pages: string[] = []
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i)
        const content = await page.getTextContent()
        const pageText = content.items
          .map((item) => ('str' in item ? item.str : ''))
          .join(' ')
        pages.push(pageText)
      }
      return pages.join('\n')
    }
    case '.docx': {
      const mammoth = await import('mammoth')
      const buffer = await file.arrayBuffer()
      const result = await mammoth.default.extractRawText({ arrayBuffer: buffer })
      return result.value
    }
    default:
      throw new Error(`Format non supporté : ${ext}`)
  }
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ChatMessage = {
  id: string
  role: 'user' | 'assistant'
  content: string
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function ModuleNoteTechnique({ compact, onHasResult }: { compact?: boolean; onHasResult?: (has: boolean) => void } = {}) {
  // -- State ----------------------------------------------------------------
  const [step, setStep] = useState(1)
  const [file, setFile] = useState<File | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [tdrContent, setTdrContent] = useState('')
  const [isExtracting, setIsExtracting] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)
  const [isRefining, setIsRefining] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const [generatedText, setGeneratedText] = useState('')
  const [generationTime, setGenerationTime] = useState(0)
  const [progress, setProgress] = useState<{ pct: number; label: string } | null>(null)
  const [refineInput, setRefineInput] = useState('')
  const [copied, setCopied] = useState(false)
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([])
  const [showRefine, setShowRefine] = useState(false)
  const [showApercuNote, setShowApercuNote] = useState(false)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const dragCounter = useRef(0)
  // Annulation de la génération en cours (bouton « Annuler »). Le flag
  // distingue un abandon volontaire d'un timeout (qui partagent AbortError).
  const generateAbortRef = useRef<AbortController | null>(null)
  const generateCancelledRef = useRef(false)

  const { limits, incrementLimit, openUpgrade, openAuth, openAuthGate, isAuthenticated } = useFreemium()
  // Sauvegarde auto + historique 48 h (anti-perte).
  const { history, saveDocument } = useModuleDocuments('note-technique', isAuthenticated)

  // Lancement en attente : un visiteur non connecté a cliqué sur « Générer »,
  // la popup d'inscription s'est affichée. Une fois connecté, on relance.
  const [pendingAuthLaunch, setPendingAuthLaunch] = useState(false)

  const hasContent = tdrContent.trim().length > 0
  const hasResult = generatedText.trim().length > 0

  useEffect(() => { onHasResult?.(hasResult) }, [hasResult, onHasResult])

  const baseName = file?.name.replace(/\.[^/.]+$/, '') ?? 'note-technique'

  // -- Restore draft from localStorage on mount -----------------------------
  // Le toast n'apparaît qu'une fois par session de navigateur (sessionStorage flag)
  // pour éviter qu'il se ré-affiche à chaque remount du composant — il y a réellement
  // un brouillon actif uniquement quand le user revient sur le module après en avoir
  // ouvert d'autres ou rechargé l'onglet.
  const [restoredFromCache, setRestoredFromCache] = useState(false)
  const [restoredFileName, setRestoredFileName] = useState<string | null>(null)

  useEffect(() => {
    try {
      // Migration : si nouvelle clé absente mais ancienne présente, on lit l'ancienne.
      let saved = localStorage.getItem(LS_KEY)
      let camefromLegacy = false
      if (!saved) {
        saved = localStorage.getItem(LEGACY_LS_KEY)
        camefromLegacy = !!saved
      }
      if (!saved) return
      const { text, fileName, time } = JSON.parse(saved) as { text: string; fileName?: string; time?: number }
      if (!text || text.trim().length === 0) {
        try { localStorage.removeItem(LS_KEY); localStorage.removeItem(LEGACY_LS_KEY) } catch { /* ignore */ }
        return
      }
      setGeneratedText(text)
      if (time) setGenerationTime(time)
      setChatHistory([{ id: crypto.randomUUID(), role: 'assistant', content: text }])
      setRestoredFromCache(true)
      setRestoredFileName(fileName ?? null)
      // Migration silencieuse : on déplace vers la nouvelle clé et on supprime l'ancienne.
      if (camefromLegacy) {
        try {
          localStorage.setItem(LS_KEY, saved)
          localStorage.removeItem(LEGACY_LS_KEY)
        } catch { /* ignore */ }
      }
      const SS_FLAG = 'qualyse_note_draft_toast_shown'
      const alreadyShown = sessionStorage.getItem(SS_FLAG) === '1'
      if (!alreadyShown) {
        sessionStorage.setItem(SS_FLAG, '1')
        toast.info('Brouillon restauré', {
          description: fileName ? `Dernière note générée pour « ${fileName} »` : 'Votre dernière note technique a été restaurée.',
        })
      }
    } catch {
      // localStorage unavailable or corrupt — silent
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // -- File handling --------------------------------------------------------
  const processFile = useCallback(async (selected: File) => {
    if (!isValidFileType(selected)) {
      toast.error('Format non supporté', {
        description: `Formats acceptés : ${ACCEPTED_EXTENSIONS.join(', ')}`,
      })
      return
    }
    const sizeMB = selected.size / (1024 * 1024)
    if (sizeMB > MAX_FILE_SIZE_MB) {
      toast.error('Fichier trop volumineux', {
        description: `Taille maximale : ${MAX_FILE_SIZE_MB} Mo`,
      })
      return
    }
    setFile(selected)
    setIsExtracting(true)
    setGeneratedText('')
    setChatHistory([])
    try {
      const text = await extractTextFromFile(selected)
      setTdrContent(text)
      toast.success('TDR chargé', {
        description: `${selected.name} (${formatFileSize(selected.size)}) — ${formatNumber(text.length)} caractères extraits`,
      })
    } catch (error) {
      console.error('[TDR] extraction échouée:', selected.name, error)
      toast.error('Erreur de lecture', {
        description: `Impossible d'extraire le texte de « ${selected.name} ». Vérifiez qu'il n'est pas protégé ou endommagé.`,
      })
      setFile(null)
      setTdrContent('')
    } finally {
      setIsExtracting(false)
    }
  }, [])

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selected = e.target.files?.[0]
      if (selected) processFile(selected)
    },
    [processFile],
  )

  const handleDragEnter = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault(); e.stopPropagation()
    dragCounter.current += 1
    if (dragCounter.current === 1) setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault(); e.stopPropagation()
    dragCounter.current -= 1
    if (dragCounter.current === 0) setIsDragging(false)
  }, [])

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault(); e.stopPropagation()
  }, [])

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault(); e.stopPropagation()
      dragCounter.current = 0
      setIsDragging(false)
      const dropped = e.dataTransfer.files?.[0]
      if (dropped) processFile(dropped)
    },
    [processFile],
  )

  // -- Generate note technique ----------------------------------------------
  const handleGenerate = useCallback(async () => {
    if (!hasContent) {
      toast.error('Veuillez charger un TDR')
      return
    }
    // Inscription obligatoire avant tout traitement : on affiche la popup et on
    // garde le lancement en attente — le TDR déjà chargé reste en mémoire.
    if (!isAuthenticated) {
      setPendingAuthLaunch(true)
      openAuthGate()
      return
    }
    if (!limits.is_pro) {
      setShowApercuNote(true)
      return
    }
    setIsGenerating(true)
    setGeneratedText('')
    setChatHistory([])
    setGenerationTime(0)
    setRestoredFromCache(false)
    setRestoredFileName(null)
    const t0 = Date.now()
    const abort = new AbortController()
    generateAbortRef.current = abort
    generateCancelledRef.current = false
    const timeoutId = setTimeout(() => abort.abort(), GENERATE_TIMEOUT_MS)

    const stages: { pct: number; label: string; delayMs: number }[] = [
      { pct:  5, label: 'Lecture du TDR en cours…',                    delayMs:     0 },
      { pct: 12, label: 'Analyse du type d\'évaluation…',               delayMs:  3000 },
      { pct: 22, label: 'Identification des niveaux d\'investigation…',  delayMs:  8000 },
      { pct: 33, label: 'Rédaction : compréhension de la mission…',      delayMs: 25000 },
      { pct: 45, label: 'Rédaction de la démarche méthodologique…',      delayMs: 60000 },
      { pct: 56, label: 'Élaboration des profils d\'enquêtés…',          delayMs: 110000 },
      { pct: 66, label: 'Estimation budgétaire en cours…',               delayMs: 170000 },
      { pct: 76, label: 'Analyse et commentaires sur les TDR…',          delayMs: 230000 },
      { pct: 85, label: 'Rédaction de la conclusion…',                   delayMs: 300000 },
      { pct: 92, label: 'Finalisation du document…',                     delayMs: 370000 },
      { pct: 97, label: 'Dernières vérifications…',                      delayMs: 440000 },
    ]
    const stageTimers: ReturnType<typeof setTimeout>[] = []
    stages.forEach(({ pct, label, delayMs }) => {
      stageTimers.push(setTimeout(() => setProgress({ pct, label }), delayMs))
    })

    try {
      const res = await fetch('/api/note-technique', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'generate', tdrContent: tdrContent.trim() }),
        signal: abort.signal,
      })
      if (!res.ok || !res.body) {
        const errData = await res.json().catch(() => ({}))
        throw new Error((errData as Record<string, string>).error || `Erreur serveur (${res.status})`)
      }
      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let fullText = ''
      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() ?? ''
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          let event: Record<string, unknown>
          try { event = JSON.parse(line.slice(6)) } catch { continue }
          if (event.type === 'delta') {
            fullText += String(event.text)
          } else if (event.type === 'done') {
            const elapsed = Math.round((Date.now() - t0) / 1000)
            setGeneratedText(fullText)
            setGenerationTime(elapsed)
            setChatHistory([{ id: crypto.randomUUID(), role: 'assistant', content: fullText }])
            try { localStorage.setItem(LS_KEY, JSON.stringify({ text: fullText, fileName: file?.name, time: elapsed })) } catch { /* quota exceeded */ }
            // Sauvegarde automatique (anti-perte + historique 48 h). Non bloquant.
            void saveDocument(file?.name ?? 'Note technique', fullText)
            await incrementLimit('note_count')
            toast.success('Note technique générée !', { description: 'Téléchargez le document Word ci-dessous.' })
            // Bascule automatiquement vers l'étape résultat après génération réussie
            setStep(3)
          } else if (event.type === 'error') {
            throw new Error(String(event.message))
          }
        }
      }
    } catch (error) {
      const isAbort = error instanceof Error && error.name === 'AbortError'
      if (generateCancelledRef.current) {
        // Abandon volontaire de l'utilisateur → message neutre, pas une erreur.
        // Le résultat n'est jamais arrivé (on coupe avant l'événement 'done')
        // → ni auto-save ni incrément.
        toast.info('Génération annulée.')
      } else if (isAbort) {
        toast.error('Échec de la génération', {
          description: 'La génération a pris trop de temps (>10 min). Essayez avec un TDR plus court.',
        })
      } else {
        toast.error('Échec de la génération', { description: friendlyError(error, 'processing') })
      }
    } finally {
      stageTimers.forEach(clearTimeout)
      clearTimeout(timeoutId)
      setProgress(null)
      setIsGenerating(false)
      generateAbortRef.current = null
    }
  }, [hasContent, tdrContent, file, limits.is_pro, openUpgrade, incrementLimit, isAuthenticated, openAuthGate, saveDocument])

  // Annule la génération en cours (interrompt la requête réseau).
  const handleCancelGenerate = useCallback(() => {
    generateCancelledRef.current = true
    generateAbortRef.current?.abort()
  }, [])

  // Reprise automatique : dès que l'utilisateur est connecté, on relance la
  // génération. Délai de stabilisation de session (cf. AUTH_RESUME_SETTLE_MS)
  // pour éviter un 401 après connexion Google.
  useEffect(() => {
    if (pendingAuthLaunch && isAuthenticated) {
      setPendingAuthLaunch(false)
      setTimeout(() => { handleGenerate() }, AUTH_RESUME_SETTLE_MS)
    }
  }, [pendingAuthLaunch, isAuthenticated, handleGenerate])

  // -- Refine ---------------------------------------------------------------
  const handleRefine = useCallback(async () => {
    if (!refineInput.trim() || !generatedText) return
    const instruction = refineInput.trim()
    setRefineInput('')
    setIsRefining(true)
    const userMsg: ChatMessage = { id: crypto.randomUUID(), role: 'user', content: instruction }
    const updatedHistory = [...chatHistory, userMsg]
    setChatHistory(updatedHistory)
    const abort = new AbortController()
    const timeoutId = setTimeout(() => abort.abort(), GENERATE_TIMEOUT_MS)
    try {
      const res = await fetch('/api/note-technique', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'refine',
          tdrContent: tdrContent.trim(),
          instruction,
          currentDocument: generatedText,
          conversationHistory: chatHistory.map((m) => ({ role: m.role, content: m.content.substring(0, 500) })),
        }),
        signal: abort.signal,
      })
      if (!res.ok || !res.body) {
        const errData = await res.json().catch(() => ({}))
        throw new Error((errData as Record<string, string>).error || `Erreur serveur (${res.status})`)
      }
      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let fullText = ''
      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() ?? ''
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          let event: Record<string, unknown>
          try { event = JSON.parse(line.slice(6)) } catch { continue }
          if (event.type === 'delta') { fullText += String(event.text) }
          else if (event.type === 'done') {
            const newHistory = [...updatedHistory, { id: crypto.randomUUID(), role: 'assistant' as const, content: fullText }]
            setGeneratedText(fullText)
            setChatHistory(newHistory)
            try { localStorage.setItem(LS_KEY, JSON.stringify({ text: fullText, fileName: file?.name, time: generationTime })) } catch { /* quota exceeded */ }
            toast.success('Document affiné')
          } else if (event.type === 'error') {
            throw new Error(String(event.message))
          }
        }
      }
    } catch (error) {
      const isTimeout = error instanceof Error && error.name === 'AbortError'
      const message = isTimeout ? 'L\'affinage a pris trop de temps. Réessayez.' : friendlyError(error, 'processing')
      toast.error('Échec de l\'affinage', { description: message })
    } finally {
      clearTimeout(timeoutId)
      setIsRefining(false)
    }
  }, [refineInput, generatedText, tdrContent, chatHistory, file, generationTime])

  // -- Copy / Download ------------------------------------------------------
  const handleCopy = useCallback(async () => {
    if (!generatedText) return
    try {
      await navigator.clipboard.writeText(generatedText)
      setCopied(true)
      toast.success('Document copié')
      setTimeout(() => setCopied(false), 2000)
    } catch { toast.error('Impossible de copier') }
  }, [generatedText])

  const handleDownloadWord = useCallback(async () => {
    if (!generatedText) return
    setIsDownloading(true)
    try {
      const res = await fetch('/api/export/docx', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: generatedText, type: 'note-technique', title: 'Note Technique Qualitative', sourceFile: file?.name ?? '' }),
      })
      if (!res.ok) throw new Error('Erreur lors de la génération du document Word.')
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${baseName}_note_technique.docx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      toast.success('Document Word téléchargé')
    } catch (error) {
      toast.error('Erreur', { description: friendlyError(error, 'export') })
    } finally {
      setIsDownloading(false)
    }
  }, [generatedText, baseName, file])

  // -- Reset ----------------------------------------------------------------
  // Confirmation avant réinitialisation — efface le brouillon sans filet.
  const [showResetConfirm, setShowResetConfirm] = useState(false)

  const handleReset = useCallback(() => {
    setFile(null)
    setTdrContent('')
    setGeneratedText('')
    setGenerationTime(0)
    setRefineInput('')
    setChatHistory([])
    setCopied(false)
    setIsExtracting(false)
    setIsGenerating(false)
    setIsRefining(false)
    setShowRefine(false)
    setShowApercuNote(false)
    setStep(1)
    setRestoredFromCache(false)
    setRestoredFileName(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
    try { localStorage.removeItem(LS_KEY); localStorage.removeItem(LEGACY_LS_KEY) } catch { /* ignore */ }
    toast.info('Module réinitialisé')
  }, [])

  const dismissRestoredBanner = useCallback(() => {
    setRestoredFromCache(false)
    setRestoredFileName(null)
    setGeneratedText('')
    setChatHistory([])
    try { localStorage.removeItem(LS_KEY); localStorage.removeItem(LEGACY_LS_KEY) } catch { /* ignore */ }
    toast.info('Résultat précédent effacé.')
  }, [])

  // =========================================================================
  // RENDER
  // =========================================================================
  return (
    <div className="mx-auto w-full max-w-4xl" style={{ position: 'relative' }}>

      {/* BLOC 1 — Header */}
      {!compact && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20, paddingBottom: 20, borderBottom: '1px solid #E5E7EB' }}>
          <div style={{ width: 48, height: 48, borderRadius: 14, background: M_BG, border: `1.5px solid ${M_COLOR}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <PencilLine size={24} color={M_COLOR} />
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, fontFamily: 'monospace', letterSpacing: '0.1em', color: M_COLOR, textTransform: 'uppercase', marginBottom: 2 }}>MODULE 01</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#1a1a1a', letterSpacing: '-0.3px', lineHeight: 1.2 }}>Note Technique Qualitative</div>
            <div style={{ fontSize: 13, color: '#6B7280', marginTop: 4 }}>Importez un Termes de Référence et générez automatiquement une proposition technique qualitative professionnelle.</div>
          </div>
        </div>
      )}

      {/* STEPPER */}
      <div className="qly-stepper" style={{ display: 'flex', gap: 6, marginBottom: 20, alignItems: 'stretch' }}>
        {M_STEPS.map((s, i) => {
          const n = i + 1
          const isActive = step === n
          const isDone = step > n
          return (
            <div key={n} className="qly-step-item" style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
              {i > 0 && (
                <span className="qly-step-sep" style={{ padding: '0 3px', fontSize: 24, color: '#BBBBBB', fontWeight: 900, flexShrink: 0, userSelect: 'none', lineHeight: 1 }}>›</span>
              )}
              <div
                onClick={() => isDone && setStep(n)}
                style={{
                  flex: 1, display: 'flex', alignItems: 'center', gap: 10,
                  padding: '12px 14px', borderRadius: 10, minWidth: 0,
                  border: isActive ? `2px solid ${M_COLOR}` : isDone ? `1.5px solid ${M_COLOR}55` : '1.5px solid #DEDEDE',
                  background: isActive ? M_BG : isDone ? `${M_COLOR}08` : 'white',
                  boxShadow: isActive ? `0 4px 14px ${M_COLOR}35` : 'none',
                  cursor: isDone ? 'pointer' : 'default', transition: 'all 0.2s',
                }}
              >
                <div style={{
                  width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: 800,
                  background: (isActive || isDone) ? M_COLOR : 'white',
                  border: (isActive || isDone) ? 'none' : '1.5px solid #C0C0C0',
                  color: (isActive || isDone) ? 'white' : '#666',
                }}>
                  {isDone ? '✓' : n}
                </div>
                <div style={{ minWidth: 0, overflow: 'hidden' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: (isActive || isDone) ? M_COLOR : '#555', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.name}</div>
                  <div className="qly-step-hint" style={{ fontSize: 10, color: '#6B7280', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.hint}</div>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* MAIN CARD */}
      <div className="qly-module-card" style={{ background: 'white', borderRadius: 20, border: '1px solid #E5E7EB', overflow: 'hidden', boxShadow: '0 4px 24px rgba(0,0,0,0.05)' }}>

        {/* STEP 1 — Importer */}
        {step === 1 && (
          <div className="qly-step-content" style={{ padding: '28px', display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Drop zone */}
            <div
              role="button" tabIndex={0} aria-label="Zone de dépôt du TDR"
              className={`relative flex min-h-[140px] cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed transition-all duration-200 ${isDragging ? 'border-tq-green bg-tq-green-lighter/60 scale-[1.01]' : file ? 'border-tq-green/40 bg-tq-green-lighter/30' : 'border-tq-border bg-tq-bg hover:border-tq-green/60 hover:bg-tq-green-lighter/20'} ${isExtracting || isGenerating ? 'pointer-events-none opacity-60' : ''}`}
              style={{ borderColor: isDragging ? M_COLOR : file ? `${M_COLOR}55` : '#D9D5CE' }}
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInputRef.current?.click() } }}
              onDragEnter={handleDragEnter} onDragLeave={handleDragLeave}
              onDragOver={handleDragOver} onDrop={handleDrop}
            >
              <input ref={fileInputRef} type="file" accept={ACCEPT_ATTR} className="hidden" onChange={handleFileChange} />
              {isExtracting ? (
                <><Loader2 className="size-10 animate-spin" style={{ color: M_COLOR }} /><p className="text-sm text-tq-muted">Extraction du texte en cours…</p></>
              ) : file ? (
                <>
                  {file.name.endsWith('.pdf') ? <FileType2 className="size-10 text-red-500" /> : <FileText className="size-10" style={{ color: M_COLOR }} />}
                  <div className="text-center">
                    <p className="text-sm font-semibold text-tq-text">{file.name}</p>
                    <p className="text-xs text-tq-muted">{formatFileSize(file.size)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button type="button" variant="ghost" size="sm" className="text-xs text-tq-muted underline-offset-4 hover:underline" onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click() }}>
                      Changer de fichier
                    </Button>
                    {hasContent && (
                      <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-700 text-[10px] px-2">
                        {formatNumber(tdrContent.length)} car. extraits
                      </Badge>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <Upload className="size-10 text-tq-muted" />
                  <div className="text-center">
                    <p className="text-sm font-medium text-tq-text">Glissez-déposez votre TDR ici</p>
                    <p className="mt-1 text-xs text-tq-muted">ou cliquez pour sélectionner</p>
                    <p className="mt-2 text-[11px] text-tq-muted">PDF, DOCX, TXT &mdash; max {MAX_FILE_SIZE_MB} Mo</p>
                  </div>
                </>
              )}
            </div>

            {/* Status */}
            <div className="flex items-center justify-between">
              <p className="text-sm text-tq-muted">
                {!file ? 'Sélectionnez un fichier TDR pour commencer.' : !hasContent ? 'Extraction en cours…' : 'TDR prêt. Passez à l\'étape suivante.'}
              </p>
              {(file || hasResult) && (
                <Button variant="ghost" size="sm" onClick={() => setShowResetConfirm(true)} disabled={isGenerating || isRefining} className="text-tq-muted hover:bg-tq-hover hover:text-tq-text">
                  <RotateCcw className="size-4" /> Nouveau projet
                </Button>
              )}
            </div>

            {/* Historique récent (48 h) — notes sauvegardées, ré-ouvrables. */}
            {!hasResult && (
              <ModuleHistoryPanel
                history={history}
                accent={M_COLOR}
                noun="note"
                onReopen={(doc) => {
                  setGeneratedText(doc.content)
                  setChatHistory([{ id: crypto.randomUUID(), role: 'assistant', content: doc.content }])
                  setStep(3)
                }}
              />
            )}
          </div>
        )}

        {/* STEP 2 — Générer */}
        {step === 2 && (
          <div className="qly-step-content" style={{ padding: '28px', display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* File summary or warning */}
            {file && hasContent ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderRadius: 10, background: M_BG, border: `1px solid ${M_COLOR}35` }}>
                <FileText size={18} color={M_COLOR} style={{ flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 13, fontWeight: 600, color: '#111', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</p>
                  <p style={{ fontSize: 11, color: '#6B7280', margin: 0 }}>{formatFileSize(file.size)} · {formatNumber(tdrContent.length)} caractères extraits</p>
                </div>
              </div>
            ) : (
              <div style={{ borderRadius: 10, border: '1px solid #fde68a', background: '#fffbeb', padding: '12px 16px', fontSize: 13, color: '#92400e' }}>
                Aucun TDR chargé. Retournez à l&apos;étape 1 pour importer votre fichier.
              </div>
            )}

            {/* Generate button */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '16px 0' }}>
              <div style={{ width: '100%', maxWidth: 520 }}>
                <CreditCostBanner moduleType="note-tech" />
              </div>
              <button
                onClick={handleGenerate}
                disabled={!hasContent || isExtracting || isGenerating || isRefining}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                  padding: '14px 32px', borderRadius: 10, border: 'none',
                  background: !hasContent || isGenerating ? '#9CA3AF' : M_COLOR,
                  color: '#fff', fontSize: 15, fontWeight: 700,
                  cursor: !hasContent || isGenerating ? 'not-allowed' : 'pointer',
                  boxShadow: hasContent && !isGenerating ? `0 4px 16px ${M_COLOR}35` : 'none',
                  transition: 'all 0.2s',
                }}
              >
                {isGenerating
                  ? <><Loader2 size={18} className="animate-spin" /> Génération en cours…</>
                  : <><Sparkles size={18} /> {hasResult ? 'Régénérer la note' : 'Générer la note technique'}</>
                }
              </button>
              {hasResult && !isGenerating && (
                <p style={{ fontSize: 13, color: M_COLOR, fontWeight: 600, margin: 0 }}>✓ Note générée — passez à l&apos;étape 3 pour télécharger</p>
              )}
            </div>

            {/* Progress block */}
            {isGenerating && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ borderRadius: 10, border: '1px solid #fde68a', background: '#fffbeb', padding: '12px 16px' }}>
                  <p style={{ fontSize: 13, fontWeight: 700, color: '#92400e', margin: '0 0 4px' }}>Génération en cours…</p>
                  <p style={{ fontSize: 12, color: '#92400e', margin: 0 }}>La rédaction d&apos;une note technique complète peut prendre jusqu&apos;à 8 minutes. Merci de ne pas fermer cette page.</p>
                </div>
                {progress && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#6B7280' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Loader2 size={12} className="animate-spin" style={{ color: M_COLOR }} />
                        {progress.label}
                      </span>
                      <span style={{ fontWeight: 700, color: M_COLOR }}>{progress.pct}%</span>
                    </div>
                    <div style={{ height: 6, background: '#E5E7EB', borderRadius: 6, overflow: 'hidden' }}>
                      <div style={{ height: '100%', borderRadius: 6, background: M_COLOR, width: `${progress.pct}%`, transition: 'width 1.2s ease-out' }} />
                    </div>
                  </div>
                )}
                <Button variant="outline" size="sm" onClick={handleCancelGenerate} className="self-start gap-1.5 text-tq-muted">
                  <X className="size-3.5" /> Annuler
                </Button>
              </div>
            )}

            {/* Aperçu gratuit */}
            {showApercuNote && !hasResult && !isGenerating && (
              <div style={{ background: '#fff', border: `2px solid ${M_COLOR}`, borderRadius: 12, padding: '24px', position: 'relative' }}>
                <div style={{ position: 'absolute', top: 16, right: 16, background: M_BG, border: `1px solid ${M_COLOR}`, borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 700, color: M_COLOR }}>
                  🔓 Aperçu gratuit
                </div>
                <div style={{ marginBottom: 20 }}>
                  <p style={{ fontSize: 13, fontWeight: 800, color: M_COLOR, margin: '0 0 4px', letterSpacing: '0.05em' }}>📋 APERÇU DE VOTRE NOTE TECHNIQUE</p>
                  <div style={{ height: 1, background: '#D1FAE5', margin: '8px 0' }} />
                  <p style={{ fontSize: 13, color: '#374151', margin: '0 0 2px' }}>Proposition technique qualitative</p>
                  {file && <p style={{ fontSize: 12, color: '#6B7280', margin: 0 }}>{file.name}</p>}
                </div>
                <div style={{ marginBottom: 20 }}>
                  <p style={{ fontSize: 12, fontWeight: 700, color: '#374151', margin: '0 0 10px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>SOMMAIRE STRUCTURÉ :</p>
                  <nav style={{ background: '#F9FAFB', borderRadius: 8, padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {([
                      { level: 1, text: '1. COMPRÉHENSION DE LA MISSION' },
                      { level: 2, text: '1.1 Positionnement du volet qualitatif dans le dispositif d\'évaluation' },
                      { level: 2, text: '1.2 Enjeux spécifiques de cette baseline' },
                      { level: 1, text: '2. DÉMARCHE MÉTHODOLOGIQUE DU VOLET QUALITATIF' },
                      { level: 2, text: '2.1 Niveau communautaire : enquête semi-structurée auprès des leaders' },
                      { level: 3, text: '2.1.1 Objectifs du programme couverts' },
                      { level: 3, text: '2.1.2 Outil et justification' },
                      { level: 3, text: '2.1.3 Profils cibles et justification institutionnelle' },
                      { level: 3, text: '2.1.4 Valeur ajoutée spécifique du qualitatif à ce niveau' },
                      { level: 2, text: '2.2 Niveau membres et ménages VSLA : discussions de groupe (FGD)' },
                      { level: 3, text: '2.2.1 Objectifs du programme couverts' },
                      { level: 3, text: '2.2.2 Outil et justification' },
                      { level: 3, text: '2.2.3 Profils cibles et justification' },
                      { level: 3, text: '2.2.4 Valeur ajoutée spécifique du qualitatif à ce niveau' },
                      { level: 2, text: '2.3 Niveau parties prenantes institutionnelles : entretiens avec informateurs clés (KII)' },
                      { level: 3, text: '2.3.1 Objectifs du programme couverts' },
                      { level: 3, text: '2.3.2 Outil et justification' },
                      { level: 3, text: '2.3.3 Profils cibles et justification institutionnelle' },
                      { level: 3, text: '2.3.4 Valeur ajoutée spécifique du qualitatif à ce niveau' },
                      { level: 2, text: '2.4 Tableau de synthèse des activités qualitatives' },
                      { level: 2, text: '2.5 Apport de l\'équipe qualitative dans la revue des outils de collecte' },
                      { level: 2, text: '2.6 Traitement et analyse des données qualitatives' },
                      { level: 2, text: '2.7 Considérations éthiques' },
                      { level: 1, text: '3. ESTIMATION BUDGÉTAIRE' },
                      { level: 1, text: '4. ANALYSE ET COMMENTAIRES SUR LES TDR' },
                      { level: 2, text: '4.1 Points forts du TDR' },
                      { level: 2, text: '4.2 Points d\'attention' },
                      { level: 1, text: '5. CONCLUSION' },
                    ] as { level: 1 | 2 | 3; text: string }[]).map((item, idx) => (
                      <div key={idx} style={{
                        display: 'flex', alignItems: 'flex-start', gap: 6,
                        paddingLeft: item.level === 1 ? 0 : item.level === 2 ? 14 : 28,
                        fontSize: item.level === 1 ? 12 : 11,
                        fontWeight: item.level === 1 ? 700 : 400,
                        color: item.level === 1 ? '#1a1a1a' : '#6B7280',
                        fontFamily: 'monospace',
                        lineHeight: 1.6,
                      }}>
                        <span style={{ color: M_COLOR, flexShrink: 0, fontSize: item.level === 1 ? 13 : 11 }}>
                          {item.level === 1 ? '▸' : '–'}
                        </span>
                        <span>{item.text}</span>
                      </div>
                    ))}
                  </nav>
                </div>
                <div style={{ background: M_BG, border: `1.5px solid ${M_COLOR}`, borderRadius: 10, padding: '18px 20px' }}>
                  <p style={{ fontSize: 13, color: '#374151', margin: '0 0 4px', fontWeight: 600 }}>🔒 Votre note technique complète avec verbatims, tableaux et analyse approfondie de votre TDR est prête à être générée.</p>
                  {/* Prix de référence UNIQUE (D-020) — « 20$ » = faute D-019,
                      « 13 000 FCFA » = montant FAUX (réel : 13 100). */}
                  <p style={{ fontSize: 13, color: M_COLOR, fontWeight: 700, margin: '12px 0 4px' }}>⭐ Plan Pro — {REFERENCE_PRICE_LABEL}</p>
                  <p style={{ fontSize: 12, color: '#6B7280', margin: '0 0 16px' }}>Montant local affiché au moment du paiement</p>
                  <button
                    onClick={isAuthenticated ? openUpgrade : () => openAuth('register')}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', padding: '13px 20px', borderRadius: 9, border: 'none', background: M_COLOR, color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                    🚀 Générer ma note complète → {isAuthenticated ? 'Passer au Pro' : 'S\'inscrire et générer'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* STEP 3 — Résultat */}
        {step === 3 && (
          <div className="qly-step-content" style={{ padding: '28px', display: 'flex', flexDirection: 'column', gap: 16 }}>
            {restoredFromCache && hasResult && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 14px', background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 8, fontSize: 13, color: '#1E40AF' }}>
                <span>
                  Résultat précédent disponible{restoredFileName ? ` — « ${restoredFileName} »` : ''} — vous pouvez le télécharger ci-dessous.
                </span>
                <button onClick={dismissRestoredBanner} style={{ background: 'transparent', border: '1px solid #BFDBFE', color: '#1E40AF', padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>Effacer</button>
              </div>
            )}
            {hasResult ? (
              <>
                {/* Success header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: '#D1FAE5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Check size={20} color="#059669" />
                  </div>
                  <div>
                    <p style={{ fontSize: 15, fontWeight: 700, color: '#111', margin: 0 }}>Note technique générée avec succès</p>
                    <p style={{ fontSize: 12, color: '#6B7280', margin: 0 }}>
                      ~{Math.max(1, Math.round(generatedText.length / 3000))} pages estimées · {formatNumber(countWords(generatedText))} mots{generationTime > 0 ? ` · Généré en ${generationTime}s` : ''}
                    </p>
                  </div>
                </div>

                {/* Document structure */}
                {(() => {
                  const structure = extractDocumentStructure(generatedText)
                  return structure.length > 0 ? (
                    <div style={{ borderRadius: 10, border: '1px solid #E5E7EB', background: '#FAFAF8', padding: '16px' }}>
                      <p style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 10px' }}>Structure du document</p>
                      <nav style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {structure.map((item, idx) => (
                          <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: item.level === 1 ? '#111' : '#6B7280', paddingLeft: item.level === 1 ? 0 : item.level === 2 ? 16 : 32, fontWeight: item.level === 1 ? 600 : 400 }}>
                            <span style={{ color: M_COLOR, flexShrink: 0 }}>{item.level === 1 ? '▸' : '–'}</span>
                            <span>{item.title}</span>
                          </div>
                        ))}
                      </nav>
                    </div>
                  ) : null
                })()}

                {/* Word download */}
                <button
                  onClick={handleDownloadWord}
                  disabled={isDownloading}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '14px 24px', borderRadius: 10, border: 'none', background: isDownloading ? '#9CA3AF' : M_COLOR, color: '#fff', fontSize: 15, fontWeight: 700, cursor: isDownloading ? 'wait' : 'pointer', boxShadow: !isDownloading ? `0 4px 16px ${M_COLOR}35` : 'none' }}
                >
                  {isDownloading ? <><Loader2 size={18} className="animate-spin" /> Génération du document…</> : <><FileDown size={18} /> Télécharger en Word (.docx)</>}
                </button>

                {/* Secondary actions */}
                <div className="flex flex-wrap items-center gap-2">
                  <Button variant="outline" size="sm" onClick={handleCopy} className="border-tq-border text-tq-text hover:bg-tq-hover hover:text-tq-text">
                    {copied ? <><Check className="size-3.5" /> Copié !</> : <><Copy className="size-3.5" /> Copier le texte</>}
                  </Button>
                  <div className="flex-1" />
                  <Button size="sm" onClick={() => setShowRefine(!showRefine)} style={{ background: M_COLOR, color: '#fff', border: 'none' }}>
                    <PencilLine className="size-3.5" /> {showRefine ? 'Masquer l\'amélioration' : 'Améliorer la note'}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setShowResetConfirm(true)} style={{ borderColor: M_COLOR, color: M_COLOR }}>
                    <RotateCcw className="size-3.5" /> Nouveau TDR
                  </Button>
                </div>

                {/* Refinement chat */}
                {showRefine && (
                  <>
                    <Separator className="bg-tq-border" />
                    {chatHistory.length > 1 && (
                      <div className="space-y-3 max-h-[250px] overflow-y-auto custom-scrollbar">
                        <p className="text-xs font-medium text-tq-muted uppercase tracking-wider">Historique d&apos;affinage</p>
                        {chatHistory.slice(-6).map((msg) => (
                          <div key={msg.id} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                            {msg.role === 'assistant' && (
                              <div className="flex size-6 shrink-0 items-center justify-center rounded-full mt-0.5" style={{ background: M_BG }}>
                                <Sparkles className="size-3" style={{ color: M_COLOR }} />
                              </div>
                            )}
                            <div className={`max-w-[80%] rounded-lg px-3 py-2 text-xs leading-relaxed ${msg.role === 'user' ? 'text-white' : 'bg-tq-bg border border-tq-border text-tq-text'}`} style={msg.role === 'user' ? { background: M_COLOR } : {}}>
                              {msg.role === 'user' ? msg.content : `${msg.content.substring(0, 150)}… (document mis à jour)`}
                            </div>
                            {msg.role === 'user' && (
                              <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-tq-bg border border-tq-border mt-0.5">
                                <span className="text-[10px] font-bold text-tq-text">V</span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex gap-2">
                      <Textarea
                        value={refineInput}
                        onChange={(e) => setRefineInput(e.target.value)}
                        className="custom-scrollbar min-h-[60px] max-h-[120px] flex-1 resize-y border-tq-border bg-tq-bg text-sm text-tq-text placeholder:text-tq-muted/60"
                        placeholder="Ex : Enrichir la section 2.1 avec d'autres profils pour les KII…"
                        disabled={isRefining}
                        onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handleRefine() } }}
                      />
                      <Button onClick={handleRefine} disabled={!refineInput.trim() || isRefining} size="sm" className="self-end" style={{ background: M_COLOR, color: '#fff', border: 'none' }}>
                        {isRefining ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
                      </Button>
                    </div>
                    <p className="text-[10px] text-tq-muted">Ctrl+Entrée pour envoyer · Le document sera intégralement mis à jour</p>
                  </>
                )}
              </>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 24px', textAlign: 'center', gap: 12 }}>
                <PencilLine size={48} color="#D1D5DB" />
                <p style={{ fontSize: 14, color: '#6B7280', maxWidth: 360, margin: 0 }}>
                  Aucune note générée. Retournez à l&apos;étape 2 pour lancer la génération.
                </p>
              </div>
            )}
          </div>
        )}

        {/* BOTTOM NAV */}
        <div className="qly-nav-bar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 28px', borderTop: '1px solid #E5E7EB', background: '#FAFAF8' }}>
          <button
            disabled={step === 1}
            onClick={() => setStep(s => s - 1)}
            style={{ padding: '10px 20px', background: 'transparent', color: step === 1 ? '#D1D5DB' : '#6B7280', border: `1.5px solid ${step === 1 ? '#E5E7EB' : '#D1D5DB'}`, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: step === 1 ? 'default' : 'pointer', fontFamily: 'inherit', transition: 'all 0.15s' }}
          >← Retour</button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 12, color: '#6B7280' }}>Étape {step} sur {M_STEPS.length}</span>
            {hasResult && (
              <button
                type="button"
                className="qly-nav-reset"
                onClick={() => setShowResetConfirm(true)}
                disabled={isGenerating || isRefining}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: '#fff', color: M_COLOR, border: `1.5px solid ${M_COLOR}`, borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: (isGenerating || isRefining) ? 'not-allowed' : 'pointer', fontFamily: 'inherit', opacity: (isGenerating || isRefining) ? 0.5 : 1 }}
              >
                <RotateCcw size={13} /> Nouveau projet
              </button>
            )}
          </div>
          {step < M_STEPS.length ? (
            <button
              onClick={() => {
                // À l'étape 2 (Générer), si aucune note n'existe encore → lancer la génération
                // (qui basculera automatiquement vers l'étape 3 à la fin). Sinon, navigation simple.
                if (step === 2 && !hasResult && !isGenerating) {
                  void handleGenerate()
                } else {
                  setStep(s => s + 1)
                }
              }}
              disabled={step === 2 && isGenerating}
              style={{ padding: '10px 24px', background: M_COLOR, color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: step === 2 && isGenerating ? 'wait' : 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 8, boxShadow: `0 2px 8px ${M_COLOR}40`, transition: 'background 0.15s', opacity: step === 2 && isGenerating ? 0.7 : 1 }}
            >
              {step === 2 && isGenerating
                ? 'Génération…'
                : step === 2 && !hasResult
                  ? 'Générer & continuer →'
                  : 'Continuer →'}
            </button>
          ) : <div style={{ width: 110 }} />}
        </div>
      </div>

      {/* Confirmation « Nouveau projet » — évite la perte accidentelle de la note. */}
      <AlertDialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Démarrer un nouveau projet ?</AlertDialogTitle>
            <AlertDialogDescription>
              La note technique actuelle sera définitivement perdue. Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setShowResetConfirm(false); handleReset(); }}>
              Démarrer un nouveau projet
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
